import { Button, Icon, Paragraph } from 'components/patterns'
import PropTypes from 'prop-types'
import React from 'react'
import { sum } from 'ramda'
import {
  ANALOG_INPUT,
  CAN_OUTPUT,
  DIGITAL_INPUT,
  DIGITAL_OUTPUT,
} from 'utils/constants'
import strings from 'utils/strings'
import { CONFLICTS_SWITCHES_WITH_SHARED_SIGNALS_VIEW } from './constants'
import {
  CenterMiddlePositionWrap,
  ConflictsBody,
  ConflictsHeader,
  ConflictsOverViewContainer,
  ConflictsOverViewHeader,
  ConflictsSummary,
  StyledHeader,
} from './AddRemoveVehicleFeatures.style'

const ConflictsOverView = ({
  unresolvedConflictsCount,
  changeView,
  handleCancel,
  numSwitchConflicts,
  hideHeader,
  hideSwitchConflicts,
  conflictsDescriptionText,
  backLabel,
}) => {
  const [
    numDigitalInputConflicts,
    numAnalogInputConflicts,
    numDigitalOutputConflicts,
    numCanOutputConflicts,
  ] = unresolvedConflictsCount

  const unresolvedConflictsSum = sum(unresolvedConflictsCount)
  const allResolved =
    unresolvedConflictsSum === 0 && (!numSwitchConflicts || hideSwitchConflicts)
  const disableSwitchConflicts = unresolvedConflictsSum > 0

  return (
    <>
      {!hideHeader && (
        <ConflictsHeader>
          <StyledHeader>{strings.resolve_conflicts}</StyledHeader>
        </ConflictsHeader>
      )}
      <ConflictsBody>
        {!hideHeader && (
          <Button link onClick={handleCancel}>
            {`< ${backLabel || strings.back_to_features}`}
          </Button>
        )}
        <CenterMiddlePositionWrap>
          <ConflictsOverViewContainer>
            <ConflictsOverViewHeader type="h3">
              {allResolved
                ? strings.no_remaining_conflicts
                : conflictsDescriptionText ||
                  strings.new_features_have_conflicts_with_features_already_on_the_vehicle}
            </ConflictsOverViewHeader>
            <ConflictsSummary>
              <div>
                {numDigitalInputConflicts ? (
                  <Icon
                    width={23}
                    height={20}
                    icon="alert_exclamation_triangle_solid"
                    fill="alertWarningBgColor"
                  />
                ) : (
                  <Icon
                    icon="check-mark-only"
                    width={27}
                    fill="alertSuccessBgColor"
                  />
                )}
                <Paragraph bold>
                  {strings.digital_input_conflicts}{' '}
                  {`(${numDigitalInputConflicts})`}
                </Paragraph>
              </div>
              <div>
                {Boolean(numDigitalInputConflicts) && (
                  <Button onClick={() => changeView(DIGITAL_INPUT)}>
                    {strings.resolve_conflicts}
                  </Button>
                )}
              </div>
            </ConflictsSummary>
            <ConflictsSummary>
              <div>
                {numAnalogInputConflicts ? (
                  <Icon
                    width={23}
                    height={20}
                    icon="alert_exclamation_triangle_solid"
                    fill="alertWarningBgColor"
                  />
                ) : (
                  <Icon
                    icon="check-mark-only"
                    width={27}
                    fill="alertSuccessBgColor"
                  />
                )}
                <Paragraph bold>
                  {strings.analog_input_conflicts}{' '}
                  {`(${numAnalogInputConflicts})`}
                </Paragraph>
              </div>
              <div>
                {Boolean(numAnalogInputConflicts) && (
                  <Button onClick={() => changeView(ANALOG_INPUT)}>
                    {strings.resolve_conflicts}
                  </Button>
                )}
              </div>
            </ConflictsSummary>
            <ConflictsSummary>
              <div>
                {numDigitalOutputConflicts ? (
                  <Icon
                    width={23}
                    height={20}
                    icon="alert_exclamation_triangle_solid"
                    fill="alertWarningBgColor"
                  />
                ) : (
                  <Icon
                    icon="check-mark-only"
                    width={27}
                    fill="alertSuccessBgColor"
                  />
                )}
                <Paragraph bold>
                  {strings.digital_output_conflicts}{' '}
                  {`(${numDigitalOutputConflicts})`}
                </Paragraph>
              </div>
              <div>
                {Boolean(numDigitalOutputConflicts) && (
                  <Button onClick={() => changeView(DIGITAL_OUTPUT)}>
                    {strings.resolve_conflicts}
                  </Button>
                )}
              </div>
            </ConflictsSummary>

            <ConflictsSummary last>
              <div>
                {numCanOutputConflicts ? (
                  <Icon
                    width={23}
                    height={20}
                    icon="alert_exclamation_triangle_solid"
                    fill="alertWarningBgColor"
                  />
                ) : (
                  <Icon
                    icon="check-mark-only"
                    width={27}
                    fill="alertSuccessBgColor"
                  />
                )}
                <Paragraph bold>
                  {strings.can_output_conflicts} {`(${numCanOutputConflicts})`}
                </Paragraph>
              </div>
              <div>
                {Boolean(numCanOutputConflicts) && (
                  <Button onClick={() => changeView(CAN_OUTPUT)}>
                    {strings.resolve_conflicts}
                  </Button>
                )}
              </div>
            </ConflictsSummary>

            {!hideSwitchConflicts && Boolean(numSwitchConflicts) && <br />}
            {!hideSwitchConflicts && (
              <ConflictsSummary
                last
                disabled={disableSwitchConflicts}
                hideTopBorder={!numSwitchConflicts}
              >
                <div>
                  {numSwitchConflicts ? (
                    <Icon
                      width={23}
                      height={20}
                      icon="alert_exclamation_triangle_solid"
                      fill="alertWarningBgColor"
                    />
                  ) : (
                    <Icon
                      icon="check-mark-only"
                      width={27}
                      fill="alertSuccessBgColor"
                    />
                  )}
                  <Paragraph bold>
                    {strings.switches_with_shared_signals}{' '}
                    {`(${numSwitchConflicts})`}
                  </Paragraph>
                </div>
                <div>
                  {Boolean(numSwitchConflicts) && (
                    <Button
                      disabled={disableSwitchConflicts}
                      onClick={() =>
                        changeView(CONFLICTS_SWITCHES_WITH_SHARED_SIGNALS_VIEW)
                      }
                    >
                      {strings.resolve_conflicts}
                    </Button>
                  )}
                </div>
              </ConflictsSummary>
            )}
          </ConflictsOverViewContainer>
        </CenterMiddlePositionWrap>
      </ConflictsBody>
    </>
  )
}

ConflictsOverView.propTypes = {
  unresolvedConflictsCount: PropTypes.arrayOf(PropTypes.number),
  changeView: PropTypes.func,
  handleCancel: PropTypes.func,
  numSwitchConflicts: PropTypes.number,
  hideHeader: PropTypes.bool,
  hideSwitchConflicts: PropTypes.bool,
  /** Optional override for the description shown when conflicts exist. Defaults to vehicle wording. */
  conflictsDescriptionText: PropTypes.string,
  /** Optional override for the back-link text. Defaults to "Back to Features". */
  backLabel: PropTypes.string,
}

export default ConflictsOverView
